/**
 * ClientStatusHistorySection — compact, collapsed-by-default audit read-out of a
 * client's roster status changes (ADO #538). QA's complaint was "there is no way
 * to answer who took this client off the roster and when" — this answers exactly
 * that, one row per change, most recent first. It is an audit trail, not a
 * feature, so it stays out of the way (collapsed) rather than competing with
 * clinical content (Details / Clinicians / Goals) above it.
 *
 * `entries` (`Client.statusEvents`) is server-ordered `changedAt: desc` (newest
 * first) — do not re-sort here. It's typed optional/undefined defensively for
 * any caller that hasn't fetched a detail response carrying it; the section
 * renders nothing in that case rather than showing an empty/broken audit list.
 */

import styled from 'styled-components';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { fullName } from '../../utils/nameUtils';
import type { ClientStatusHistoryEntry } from '../../types/clients';
import { CLIENT_STATUS_LABEL } from '../../pages/ClientManagementPage/clientFormConstants';

interface ClientStatusHistorySectionProps {
  clientId: string;
  entries: ClientStatusHistoryEntry[] | undefined;
}

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Entry = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Transition = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const MetaLine = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  overflow-wrap: anywhere;
`;

const ReasonLine = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-style: italic;
  overflow-wrap: anywhere;
`;

const EmptyState = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ClientStatusHistorySection({ clientId, entries }: ClientStatusHistorySectionProps) {
  // Not yet wired on the API response — render nothing rather than a broken/empty audit section.
  if (entries === undefined) return null;

  return (
    <CollapsibleSection
      title="Status History"
      storageKey={`client-status-history:${clientId}`}
      defaultOpen={false}
    >
      {entries.length === 0 ? (
        <EmptyState>No status changes recorded.</EmptyState>
      ) : (
        <List>
          {entries.map((entry) => {
            const fromLabel = entry.fromStatus ? CLIENT_STATUS_LABEL[entry.fromStatus] : 'Created';
            const toLabel = CLIENT_STATUS_LABEL[entry.toStatus];
            const who = fullName(entry.changedBy);
            return (
              <Entry key={entry.id}>
                <Transition>
                  {fromLabel} → {toLabel}
                </Transition>
                <MetaLine>
                  {who} · {formatWhen(entry.changedAt)}
                </MetaLine>
                {entry.reason && <ReasonLine>“{entry.reason}”</ReasonLine>}
              </Entry>
            );
          })}
        </List>
      )}
    </CollapsibleSection>
  );
}

export default ClientStatusHistorySection;
